import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, X, SlidersHorizontal, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  InstantSearchProvider,
  SearchBox,
  Hits,
  RefinementList,
  PropsFacet,
  Pagination,
  SortBy,
  RangeSlider,
  Stats,
  CurrentRefinements,
  Configure,
} from '@/components/search';

// Sort options for Algolia (uses replica indices)
const SORT_OPTIONS = [
  { value: 'sequences', label: 'Relevance' },
  { value: 'sequences', label: 'Most Popular' },
  { value: 'sequences_created_at_desc', label: 'Newest First' },
  { value: 'sequences_price_asc', label: 'Price: Low to High' },
  { value: 'sequences_price_desc', label: 'Price: High to Low' },
];

// Transform boolean price type values to user-friendly labels
const transformPriceTypeLabels = (items: any[]) => {
  return items.map(item => ({
    ...item,
    label: item.value === true || item.value === 'true' ? 'Free' : 'Paid'
  }));
};

interface ProfileSequencesTabProps {
  userId: string;
  username: string;
  isOwner?: boolean;
  isPublicView?: boolean;
}

export const ProfileSequencesTab: React.FC<ProfileSequencesTabProps> = ({
  userId,
  username,
  isOwner = false,
  isPublicView = false
}) => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <InstantSearchProvider indexName="sequences_v2" routing={false}>
      {/* Pre-filter results to only show this user's sequences */}
      <Configure
        hitsPerPage={12}
        filters={`creator_id:${userId}`}
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold">
              {isOwner ? 'My Sequences' : `${username}'s Sequences`}
            </h3>
            <div className="text-muted-foreground">
              <Stats />
            </div>
          </div>

          {/* Mobile filter toggle */}
          <div className="lg:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>

        {/* Search bar and sort */}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <SearchBox
              placeholder={`Search ${username}'s sequences...`}
              className="w-full"
            />
          </div>
          <div className="hidden sm:block">
            <SortBy items={SORT_OPTIONS} />
          </div>
        </div>

        {/* Active Filters */}
        <CurrentRefinements />

        <div className="flex gap-6">
          {/* Filter Sidebar */}
          <aside className={cn(
            "w-80 shrink-0 sticky top-4 h-fit",
            !showFilters && "hidden lg:block"
          )}>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Filters</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-1">
                {/* Software Filter */}
                <RefinementList
                  attribute="software_type"
                  title="Software"
                  expanded={true}
                  limit={50}
                />

                <Separator className="my-4" />

                {/* Holiday Filter */}
                <RefinementList
                  attribute="holiday_type"
                  title="Holiday"
                  expanded={true}
                  limit={50}
                />

                <Separator className="my-4" />

                {/* Genre Filter */}
                <RefinementList
                  attribute="genres"
                  title="Genres"
                  expanded={true}
                  limit={50}
                  searchable={true}
                  searchablePlaceholder="Search genres..."
                />

                <Separator className="my-4" />

                {/* Artist Filter */}
                <RefinementList
                  attribute="artist_name"
                  title="Artists"
                  expanded={false}
                  limit={50}
                  searchable={true}
                  searchablePlaceholder="Search artists..."
                  showMore={true}
                  showMoreLimit={500}
                />

                <Separator className="my-4" />

                {/* Album Filter */}
                <RefinementList
                  attribute="album"
                  title="Albums"
                  expanded={false}
                  limit={50}
                  searchable={true}
                  searchablePlaceholder="Search albums..."
                  showMore={true}
                  showMoreLimit={500}
                />

                <Separator className="my-4" />

                {/* Props Filter */}
                <PropsFacet expanded={false} />

                <Separator className="my-4" />

                {/* Price Range */}
                <RangeSlider
                  attribute="price"
                  title="Price Range"
                  expanded={true}
                  min={0}
                  max={100}
                  step={5}
                  precision={2}
                />

                <Separator className="my-4" />

                {/* Price Type Filter */}
                <RefinementList
                  attribute="is_free"
                  title="Price Type"
                  expanded={true}
                  limit={10}
                  transformItems={transformPriceTypeLabels}
                />
              </CardContent>
            </Card>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0 space-y-6">
            <Hits
              onClearFilters={() => {}}
              emptyComponent={
                isOwner ? (
                  <div className="text-center py-12">
                    <h2 className="text-2xl font-bold mb-4">Share Your First Sequence</h2>
                    <p className="text-muted-foreground mb-6">
                      Upload your holiday light sequences and share them with the community!
                    </p>
                    <Button asChild size="lg">
                      <Link to="/upload-sequence">
                        <PlusCircle className="mr-2" size={16} />
                        Upload Your First Sequence
                      </Link>
                    </Button>
                  </div>
                ) : undefined
              }
            />

            <Pagination className="pb-8" />
          </div>
        </div>

        {/* Mobile Filters Overlay */}
        {showFilters && (
          <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-xs">
            <div className="fixed inset-y-0 left-0 w-80 bg-background border-r overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Filters</h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <RefinementList
                    attribute="software_type"
                    title="Software"
                    expanded={true}
                    limit={50}
                  />
                  <RefinementList
                    attribute="holiday_type"
                    title="Holiday"
                    expanded={true}
                    limit={50}
                  />
                  <RefinementList
                    attribute="genres"
                    title="Genres"
                    expanded={true}
                    limit={50}
                    searchable={true}
                    searchablePlaceholder="Search genres..."
                  />
                  <RefinementList
                    attribute="artist_name"
                    title="Artists"
                    expanded={false}
                    limit={50}
                    searchable={true}
                    searchablePlaceholder="Search artists..."
                    showMore={true}
                    showMoreLimit={500}
                  />
                  <RefinementList
                    attribute="album"
                    title="Albums"
                    expanded={false}
                    limit={50}
                    searchable={true}
                    searchablePlaceholder="Search albums..."
                    showMore={true}
                    showMoreLimit={500}
                  />
                  <PropsFacet expanded={false} />
                  <RangeSlider
                    attribute="price"
                    title="Price Range"
                    expanded={true}
                    min={0}
                    max={100}
                    step={5}
                  />
                  <RefinementList
                    attribute="is_free"
                    title="Price Type"
                    expanded={true}
                    transformItems={transformPriceTypeLabels}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </InstantSearchProvider>
  );
};

export default ProfileSequencesTab;
